/**
 * What a patient's name may contain — one rule, for every screen that edits one.
 *
 * It is the PATIENT APP's rule (zyla_flutter constants/common_constants.dart),
 * which is itself a mirror of the server's: that file points at api-user-service
 * `validators/patient.go`, whose VALID_CHARACTERS and 100-character cap are
 * applied to the trimmed value. The app is the stricter of the two — it leaves
 * out the digits and the "_" the server would take — and it is what these
 * screens follow, so a name accepted here is accepted everywhere it goes.
 *
 * Lives on its own rather than inside one of the dialogs because three separate
 * screens edit a name: phr's patient-details dialog and its section rail (which
 * the AI transcript renders too), and bridge's profile-v2 Identity & contact.
 */

const asText = (v) => (v === null || v === undefined ? "" : String(v));

/**
 * A NAME, as the patient app defines one — and the app's rule is itself a
 * mirror of the server's (zyla_flutter constants/common_constants.dart, whose
 * comment points at api-user-service `validators/patient.go`: VALID_CHARACTERS
 * and a 100-character cap, measured on the trimmed value).
 *
 * THE TWO FIELDS DIFFER BY ONE CHARACTER, deliberately. A first name holds no
 * SPACE — a multi-word name is split so everything after the first word lands
 * in the last name — while a last name allows one, so a middle name can ride
 * along there ("Balwinder" / "Singh Chadda"). Dots, hyphens and apostrophes are
 * name characters in both ("A.", "Anne-Marie", "O'Brien").
 *
 * The server is looser still (it also accepts digits and "_"); the app is the
 * stricter of the two and is what these screens follow, so a name accepted here
 * is accepted everywhere it goes.
 */
const FIRST_NAME_RE = /^[A-Za-z.\-']{1,100}$/;
const LAST_NAME_RE = /^[A-Za-z.\-' ]{1,100}$/;

/** Both backends cap a name at 100 (api-user-service, api-family-member-service). */
export const NAME_MAX = 100;

/**
 * What a name field accepts WHILE IT IS BEING TYPED — the app filters the
 * keystrokes rather than only complaining afterwards
 * (`nameFieldFormatters`/`nameFieldMaxLength`), so a character that could
 * never be saved never appears. Paste goes through the same filter.
 */
export const nameFilter = (field, value) => {
  const drop = field === "lastName" ? /[^A-Za-z.\-' ]/g : /[^A-Za-z.\-']/g;
  return String(value === null || value === undefined ? "" : value)
    .replace(drop, "")
    .slice(0, NAME_MAX);
};

/**
 * "" when the value is a name, else the message to print under the field — the
 * app's own wording, from its `isPayloadValid`.
 *
 * Kept alongside the filter rather than replaced by it: the filter cannot make
 * a REQUIRED field non-empty, and a value that arrived some other way (a draft,
 * a record written before any of this) still has to be judged.
 */
export const nameError = (field, value) => {
  const label = field === "lastName" ? "last name" : "first name";
  const v = asText(value).trim();
  /* An empty LAST name is not an error — the app skips a field that is
     optional and empty, and so does the server. */
  if (!v) return field === "lastName" ? "" : `Please enter a valid ${label}`;
  const re = field === "lastName" ? LAST_NAME_RE : FIRST_NAME_RE;
  return re.test(v) ? "" : `Please enter a valid ${label}`;
};

/** The two name fields, checked together. {} when both are acceptable. */
export const nameErrors = (form) => {
  const out = {};
  const first = nameError("firstName", form.firstName);
  const last = nameError("lastName", form.lastName);
  if (first) out.firstName = first;
  if (last) out.lastName = last;
  return out;
};
